import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Calendar, 
  BookMarked,
  BookOpen, 
  Heart, 
  Bell, 
  ExternalLink, 
  Clock,
  Building,
  Globe,
  FileText,
  Tag,
  DollarSign,
  Star,
  Info,
  User,
  Hash,
  Eye,
  Users
} from 'lucide-react';
import Footer from '@/components/Footer';
import ShareButtons from '@/components/ShareButtons';
import BookRecommendations from '@/components/BookRecommendations';
import AddToCalendar from '@/components/AddToCalendar';

/**
 * Format text with proper paragraph spacing and styling
 */
const FormattedText = ({ text }) => {
  if (!text) return null;
  
  // Split by double newlines (paragraphs) or single newlines
  const paragraphs = text.split(/\n\n+/).filter(p => p.trim());
  
  return (
    <div className="space-y-4">
      {paragraphs.map((paragraph, index) => {
        // Check if it's a list item (starts with - or • or number)
        const lines = paragraph.split('\n').filter(l => l.trim());
        const isList = lines.every(line => 
          /^[\-•●○◦▪▫]\s/.test(line.trim()) || 
          /^\d+[\.\)]\s/.test(line.trim())
        );
        
        if (isList) {
          return (
            <ul key={index} className="list-disc list-inside space-y-1 text-[#4B4C52] pl-2">
              {lines.map((line, i) => (
                <li key={i} className="leading-relaxed">
                  {line.replace(/^[\-•●○◦▪▫\d\.\)]+\s*/, '')}
                </li>
              ))}
            </ul>
          );
        }
        
        // Check if it looks like a heading (short line, possibly all caps or ends with :)
        const isHeading = paragraph.length < 100 && 
          (paragraph === paragraph.toUpperCase() || 
           paragraph.endsWith(':') ||
           /^(TEMA|EIXO|OBJETIVO|PRAZO|DATA|NORMAS|SUBMISSÃO)/i.test(paragraph));
        
        if (isHeading) {
          return (
            <h3 key={index} className="font-semibold text-gray-800 mt-6 mb-2">
              {paragraph}
            </h3>
          );
        }
        
        // Regular paragraph - handle internal line breaks
        return (
          <p key={index} className="text-[#4B4C52] leading-7 text-justify">
            {lines.map((line, i) => (
              <span key={i}>
                {line}
                {i < lines.length - 1 && <br />}
              </span>
            ))}
          </p>
        );
      })}
    </div>
  );
};

/**
 * Component that displays the description text
 */
const DescriptionWithAd = ({ description }) => {
  if (!description) {
    return (
      <div className="text-[#4B4C52] leading-relaxed">
        Descrição não disponível.
      </div>
    );
  }

  return <FormattedText text={description} />;
};

const CallDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [call, setCall] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFavorite, setIsFavorite] = useState(false);
  const [requiresLogin, setRequiresLogin] = useState(false);
  const [relatedCalls, setRelatedCalls] = useState([]);

  useEffect(() => {
    // Check if user has already viewed calls (5 free views policy)
    const MAX_FREE_VIEWS = 5;
    
    if (!user) {
      const viewedCalls = JSON.parse(sessionStorage.getItem('viewedCalls') || '[]');
      
      if (viewedCalls.length >= MAX_FREE_VIEWS && !viewedCalls.includes(id)) {
        // User has already viewed 5 different calls, require login
        setRequiresLogin(true);
        setLoading(false);
        return;
      }
      
      // Track this call as viewed
      if (!viewedCalls.includes(id)) {
        viewedCalls.push(id);
        sessionStorage.setItem('viewedCalls', JSON.stringify(viewedCalls));
      }
    }
    
    fetchCallDetails();
    if (user) {
      checkFavoriteStatus();
    }
  }, [id, user]);

  const fetchCallDetails = async () => {
    setLoading(true);
    try {
      // Use fetch directly for public access (no auth required)
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/calls/${id}`);
      if (!response.ok) {
        throw new Error('Call not found');
      }
      const data = await response.json();
      setCall(data);
      
      // Fetch related calls
      fetchRelatedCalls();
    } catch (error) {
      toast.error('Chamada não encontrada');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedCalls = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/calls/${id}/related`);
      if (response.ok) {
        const data = await response.json();
        setRelatedCalls(data.related_calls || []);
      }
    } catch (error) {
      console.error('Failed to fetch related calls');
    }
  };

  const checkFavoriteStatus = async () => {
    if (!user) return;
    try {
      const response = await api.get('/api/favorites');
      const favoriteIds = response.data.favorites.map(f => f.call_id);
      setIsFavorite(favoriteIds.includes(id));
    } catch (error) {
      console.error('Failed to check favorite status');
    }
  };

  const toggleFavorite = async () => {
    if (!user) {
      toast.error('Faça login para adicionar aos favoritos');
      navigate('/auth');
      return;
    }
    try {
      if (isFavorite) {
        await api.delete(`/api/favorites/${id}`);
        setIsFavorite(false);
        toast.success('Removido dos favoritos');
      } else {
        await api.post(`/api/favorites?call_id=${id}`);
        setIsFavorite(true);
        toast.success('Adicionado aos favoritos');
      }
    } catch (error) {
      toast.error('Erro ao atualizar favoritos');
    }
  };

  const createReminder = async () => {
    if (!user) {
      toast.error('Faça login para criar lembretes');
      navigate('/auth');
      return;
    }
    try {
      await api.post(`/api/reminders?call_id=${id}`);
      toast.success('Lembrete criado! Você receberá emails 7 dias e 1 dia antes do prazo.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar lembrete');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatShortDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR');
  };

  const getDaysUntilDeadline = (dateStr) => {
    if (!dateStr) return null;
    const deadline = new Date(dateStr);
    const today = new Date();
    const diffTime = deadline - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getQualisClass = (qualis) => {
    return `qualis-${qualis?.toLowerCase()}`;
  };

  const getPublicationType = (type) => {
    const types = {
      'revista': 'Revista Científica',
      'congresso': 'Congresso/Evento',
      'livro': 'Capítulo de Livro',
      'edital': 'Edital de Pesquisa'
    };
    return types[type] || type || 'Revista Científica';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F9F8]">
        <div className="text-center">
          <div className="animate-spin h-8 w-8 border-4 border-[#002FA7] border-t-transparent rounded-full mx-auto mb-4"></div>
          <p>Carregando detalhes...</p>
        </div>
      </div>
    );
  }

  // Login required screen (after 1 free view)
  if (requiresLogin) {
    const viewedCalls = JSON.parse(sessionStorage.getItem('viewedCalls') || '[]');
    
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#001f75] to-[#002FA7] p-4">
        <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md text-center">
          <div className="flex items-center justify-center h-16 w-16 rounded-full bg-[#002FA7] text-white mx-auto mb-6">
            <BookMarked className="h-8 w-8" strokeWidth={1.5} />
          </div>
          <h1 className="text-2xl font-bold mb-2">Crie sua conta gratuita</h1>
          <p className="text-[#4B4C52] mb-4">
            Você visualizou {viewedCalls.length} de 5 chamadas gratuitas.
          </p>
          <p className="text-[#4B4C52] mb-6">
            Para continuar acessando todas as chamadas para publicações científicas, faça login com sua conta Google.
          </p>
          <div className="space-y-4">
            <Button
              className="w-full bg-white hover:bg-gray-50 text-gray-700 border-2 border-gray-200 py-6 flex items-center justify-center gap-3"
              onClick={() => navigate('/auth')}
            >
              <svg className="w-6 h-6" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Entrar com Google
            </Button>
            <Button
              variant="ghost"
              className="w-full"
              onClick={() => navigate('/')}
            >
              Voltar para a página inicial
            </Button>
          </div>
          <p className="mt-6 text-xs text-[#4B4C52]">
            É 100% gratuito. Encontre chamadas filtradas por QUALIS, área do conhecimento e prazo.
          </p>
        </div>
      </div>
    );
  }

  if (!call) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F9F8]">
        <div className="text-center">
          <p className="text-lg mb-4">Chamada não encontrada</p>
          <Button onClick={() => navigate('/')}>
            Voltar ao Início
          </Button>
        </div>
      </div>
    );
  }

  const daysUntilDeadline = getDaysUntilDeadline(call.deadline);
  const shareUrl = call.slug 
    ? `${window.location.origin}/chamada/${call.slug}` 
    : `${window.location.origin}/call/${call.call_id}`;
  const pageTitle = call.title 
    ? `${call.title} - ${call.journal_name} (QUALIS ${call.qualis_classification}) | Quero Publicar` 
    : 'Quero Publicar';
  const metaDescription = call.title 
    ? `Chamada para artigos: ${call.title}. Revista ${call.journal_name || ''}, classificação QUALIS ${call.qualis_classification}. ${call.continuous_flow ? 'Submissão em fluxo contínuo.' : `Prazo: ${formatShortDate(call.deadline)}.`} Encontre oportunidades de publicação científica.`
    : 'Chamada para publicação científica';
  
  // Schema.org structured data for SEO
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Event",
    "name": call.title,
    "description": call.description?.substring(0, 300) + '...',
    "url": shareUrl,
    "eventStatus": "https://schema.org/EventScheduled",
    "eventAttendanceMode": "https://schema.org/OnlineEventAttendanceMode",
    "organizer": {
      "@type": "Organization",
      "name": call.journal_name,
      "url": call.submission_url
    },
    "location": {
      "@type": "VirtualLocation",
      "url": call.submission_url
    },
    ...(call.deadline && !call.continuous_flow && {
      "startDate": new Date().toISOString().split('T')[0],
      "endDate": call.deadline
    }),
    "offers": {
      "@type": "Offer",
      "price": call.is_free ? "0" : "",
      "priceCurrency": "BRL",
      "availability": "https://schema.org/InStock"
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F9F8] flex flex-col">
      {/* SEO Meta Tags and Schema.org Structured Data */}
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={metaDescription} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={shareUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:site_name" content="Quero Publicar" />
        <meta property="og:locale" content="pt_BR" />
        <meta property="article:published_time" content={call.created_at} />
        <meta property="article:section" content="Publicação Científica" />
        {call.knowledge_areas?.map((area, i) => (
          <meta key={i} property="article:tag" content={area} />
        ))}
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={shareUrl} />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={metaDescription} />
        
        {/* Additional SEO */}
        <meta name="keywords" content={`${call.journal_name}, QUALIS ${call.qualis_classification}, ${call.knowledge_areas?.join(', ') || ''}, publicação científica, submissão artigos, revista científica brasil`} />
        <link rel="canonical" href={shareUrl} />
        
        {/* Schema.org Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(schemaData)}
        </script>
      </Helmet>

      {/* Header for non-authenticated users */}
      {!user && (
        <header className="bg-white border-b border-[#E4E4E7]">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3">
            <div className="flex items-center justify-between">
              {/* Logo */}
              <div 
                className="flex items-center gap-2 cursor-pointer" 
                onClick={() => navigate('/')}
              >
                <div className="h-9 w-9 rounded-lg bg-[#002FA7] flex items-center justify-center">
                  <BookMarked className="h-5 w-5 text-white" strokeWidth={1.5} />
                </div>
                <span className="font-bold text-lg text-[#002FA7]">Quero Publicar</span>
              </div>

              {/* Navigation */}
              <nav className="hidden md:flex items-center gap-6">
                <button 
                  onClick={() => navigate('/blog')} 
                  className="text-sm text-gray-600 hover:text-[#002FA7] transition-colors"
                >
                  Blog
                </button>
                <button 
                  onClick={() => navigate('/about')} 
                  className="text-sm text-gray-600 hover:text-[#002FA7] transition-colors"
                >
                  Sobre
                </button>
                <button 
                  onClick={() => navigate('/contact')} 
                  className="text-sm text-gray-600 hover:text-[#002FA7] transition-colors"
                >
                  Contato
                </button>
              </nav>

              {/* CTA Button */}
              <div className="flex items-center gap-2">
                {call && <ShareButtons call={call} compact={true} />}
                <Button
                  onClick={() => navigate('/auth')}
                  className="bg-[#002FA7] hover:bg-[#001f75] text-sm"
                  data-testid="header-login-btn"
                >
                  Entrar / Cadastrar
                </Button>
              </div>
            </div>
          </div>
          
          {/* Promotional banner */}
          <div className="bg-gradient-to-r from-[#002FA7] to-[#0050FF] text-white py-2 px-4 text-center">
            <p className="text-sm">
              Crie sua conta <strong>gratuita</strong> para favoritar chamadas, receber alertas por email e acessar busca com IA!
            </p>
          </div>
        </header>
      )}

      {/* Header for authenticated users */}
      {user && (
        <header className="bg-white border-b border-[#E4E4E7] sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
            <div className="flex items-center justify-between">
              <Button
                data-testid="back-btn"
                variant="ghost"
                onClick={() => navigate('/dashboard')}
                className="gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Voltar
              </Button>
              <div className="flex items-center gap-2">
                <ShareButtons call={call} compact={false} />
                <Button
                  data-testid="favorite-btn"
                  variant="ghost"
                  size="icon"
                  onClick={toggleFavorite}
                >
                  <Heart
                    className={`h-5 w-5 ${isFavorite ? 'fill-red-500 text-red-500' : ''}`}
                  />
                </Button>
              </div>
            </div>
          </div>
        </header>
      )}

      {/* Main Content */}
      <main className="flex-1 max-w-4xl mx-auto px-4 sm:px-6 py-8 w-full">
        {/* Call Header */}
        <div className="bg-white border border-[#E4E4E7] rounded-lg p-6 sm:p-8 mb-6">
          {/* Badges */}
          <div className="flex flex-wrap gap-2 mb-4">
            <Badge className={`${getQualisClass(call.qualis_classification)} font-semibold text-sm px-3 py-1`}>
              QUALIS {call.qualis_classification}
            </Badge>
            {call.continuous_flow && (
              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 px-3 py-1">
                <Clock className="h-3 w-3 mr-1" />
                Fluxo Contínuo
              </Badge>
            )}
            {call.is_free ? (
              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 px-3 py-1">
                <DollarSign className="h-3 w-3 mr-1" />
                Gratuito
              </Badge>
            ) : (
              <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200 px-3 py-1">
                <DollarSign className="h-3 w-3 mr-1" />
                Taxa de Publicação
              </Badge>
            )}
            {call.featured && (
              <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200 px-3 py-1">
                <Star className="h-3 w-3 mr-1" />
                Destaque
              </Badge>
            )}
            {call.publication_type && (
              <Badge variant="outline" className="px-3 py-1">
                <Tag className="h-3 w-3 mr-1" />
                {getPublicationType(call.publication_type)}
              </Badge>
            )}
          </div>

          {/* Stats: Views and Reminders */}
          <div className="flex flex-wrap gap-4 mb-4 text-sm text-[#4B4C52]">
            <div className="flex items-center gap-1.5">
              <Eye className="h-4 w-4" />
              <span><strong>{call.views || 0}</strong> visualizações</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Bell className="h-4 w-4" />
              <span><strong>{call.reminder_count || 0}</strong> pessoas ativaram alerta</span>
            </div>
          </div>

          {/* Title */}
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#0A0A0A] mb-3">
            {call.title}
          </h1>

          {/* Journal Name */}
          {call.journal_name && (
            <p className="text-lg text-[#4B4C52] mb-2 flex items-center gap-2">
              <Building className="h-5 w-5 flex-shrink-0" />
              <span>{call.journal_name}</span>
            </p>
          )}

          {/* ISSN */}
          {call.issn && (
            <p className="text-sm text-[#4B4C52] mb-4 flex items-center gap-2">
              <Hash className="h-4 w-4 flex-shrink-0" />
              <span>ISSN: {call.issn}</span>
            </p>
          )}

          {/* Deadline Alert */}
          {!call.continuous_flow && call.deadline && (
            <div className={`rounded-lg p-4 mb-4 ${
              daysUntilDeadline <= 7 
                ? 'bg-red-50 border border-red-200' 
                : daysUntilDeadline <= 30 
                  ? 'bg-yellow-50 border border-yellow-200'
                  : 'bg-blue-50 border border-blue-200'
            }`}>
              <div className="flex items-center gap-3">
                <Calendar className={`h-5 w-5 flex-shrink-0 ${
                  daysUntilDeadline <= 7 
                    ? 'text-red-600' 
                    : daysUntilDeadline <= 30 
                      ? 'text-yellow-600'
                      : 'text-blue-600'
                }`} />
                <div>
                  <p className="font-semibold">
                    Prazo: {formatDate(call.deadline)}
                  </p>
                  {daysUntilDeadline > 0 ? (
                    <p className="text-sm">
                      Faltam <strong>{daysUntilDeadline} dias</strong> para o encerramento
                    </p>
                  ) : daysUntilDeadline === 0 ? (
                    <p className="text-sm text-red-600 font-semibold">
                      Último dia para submissão!
                    </p>
                  ) : (
                    <p className="text-sm text-red-600">
                      Prazo encerrado há {Math.abs(daysUntilDeadline)} dias
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3">
            {/* Google Calendar - only for logged users */}
            <AddToCalendar call={call} user={user} />
            
            {!call.continuous_flow && call.deadline && daysUntilDeadline > 0 && (
              <Button
                data-testid="reminder-btn"
                variant="outline"
                onClick={createReminder}
                className="gap-2"
              >
                <Bell className="h-4 w-4" />
                Criar Lembrete
              </Button>
            )}
            {call.submission_url && (
              <Button
                data-testid="external-link-btn"
                className="bg-[#002FA7] hover:bg-[#001f75] gap-2"
                onClick={() => window.open(call.submission_url, '_blank')}
              >
                <ExternalLink className="h-4 w-4" />
                Acessar Página da Chamada
              </Button>
            )}
          </div>
        </div>

        {/* Description Section */}
        <div className="bg-white border border-[#E4E4E7] rounded-lg p-6 sm:p-8 mb-6">
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <FileText className="h-5 w-5 text-[#002FA7]" />
            Descrição da Chamada
          </h2>
          <DescriptionWithAd description={call.description} />
        </div>

        {/* Additional Info Section */}
        {call.additional_info && (
          <div className="bg-white border border-[#E4E4E7] rounded-lg p-6 sm:p-8 mb-6">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Info className="h-5 w-5 text-[#002FA7]" />
              Informações Adicionais
            </h2>
            <div className="prose prose-sm max-w-none text-[#4B4C52] whitespace-pre-wrap leading-relaxed">
              {call.additional_info}
            </div>
          </div>
        )}

        {/* Details Grid */}
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          {/* Knowledge Areas */}
          {call.knowledge_areas && call.knowledge_areas.length > 0 && (
            <div className="bg-white border border-[#E4E4E7] rounded-lg p-6">
              <h3 className="font-bold mb-3 flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-[#002FA7]" />
                Áreas do Conhecimento
              </h3>
              <div className="flex flex-wrap gap-2">
                {call.knowledge_areas.map((area, index) => (
                  <Badge key={index} variant="outline" className="px-3 py-1">
                    {area}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Technical Details */}
          <div className="bg-white border border-[#E4E4E7] rounded-lg p-6">
            <h3 className="font-bold mb-3 flex items-center gap-2">
              <Globe className="h-5 w-5 text-[#002FA7]" />
              Detalhes Técnicos
            </h3>
            <ul className="space-y-3 text-sm text-[#4B4C52]">
              <li className="flex items-start gap-2">
                <span className="font-semibold min-w-[120px]">Classificação:</span>
                <Badge className={`${getQualisClass(call.qualis_classification)} font-semibold`}>
                  QUALIS {call.qualis_classification}
                </Badge>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-semibold min-w-[120px]">Tipo:</span>
                <span>{getPublicationType(call.publication_type)}</span>
              </li>
              {call.issn && (
                <li className="flex items-start gap-2">
                  <span className="font-semibold min-w-[120px]">ISSN:</span>
                  <span className="font-mono">{call.issn}</span>
                </li>
              )}
              <li className="flex items-start gap-2">
                <span className="font-semibold min-w-[120px]">Taxa:</span>
                <span>{call.is_free ? 'Gratuito (sem taxas)' : 'Possui taxa de publicação'}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-semibold min-w-[120px]">Submissão:</span>
                <span>{call.continuous_flow ? 'Fluxo contínuo (sem prazo fixo)' : `Até ${formatShortDate(call.deadline)}`}</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Metadata Section */}
        <div className="bg-white border border-[#E4E4E7] rounded-lg p-6 mb-6">
          <h3 className="font-bold mb-3 flex items-center gap-2">
            <User className="h-5 w-5 text-[#002FA7]" />
            Informações de Publicação
          </h3>
          <div className="grid sm:grid-cols-2 gap-4 text-sm text-[#4B4C52]">
            {call.created_at && (
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 flex-shrink-0" />
                <span><strong>Publicado em:</strong> {formatShortDate(call.created_at)}</span>
              </div>
            )}
            {call.updated_at && (
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 flex-shrink-0" />
                <span><strong>Atualizado em:</strong> {formatShortDate(call.updated_at)}</span>
              </div>
            )}
            {call.submission_url && (
              <div className="flex items-center gap-2 sm:col-span-2">
                <ExternalLink className="h-4 w-4 flex-shrink-0" />
                <span><strong>URL:</strong> <a href={call.submission_url} target="_blank" rel="noopener noreferrer" className="text-[#002FA7] hover:underline break-all">{call.submission_url}</a></span>
              </div>
            )}
          </div>
        </div>

        {/* Amazon Books Recommendations */}
        {/* Book Recommendations - Only for admin calls */}
        {call.is_admin_call && (
          <BookRecommendations 
            knowledgeAreas={call.knowledge_areas}
            isAdminCall={call.is_admin_call}
          />
        )}

        {/* Related Calls - "Você também pode gostar" */}
        {relatedCalls.length > 0 && (
          <div className="bg-gradient-to-br from-[#002FA7]/5 to-[#002FA7]/10 border border-[#002FA7]/20 rounded-lg p-6 sm:p-8">
            <h3 className="font-bold text-xl mb-6 flex items-center gap-2 text-[#002FA7]">
              <Star className="h-5 w-5" />
              Você também pode gostar
            </h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {relatedCalls.map((relatedCall) => {
                const deadline = relatedCall.deadline ? new Date(relatedCall.deadline) : null;
                const daysLeft = deadline ? Math.ceil((deadline - new Date()) / (1000 * 60 * 60 * 24)) : null;
                
                return (
                  <div 
                    key={relatedCall.call_id}
                    className="bg-white rounded-lg p-4 border border-[#E4E4E7] hover:shadow-lg hover:border-[#002FA7]/30 transition-all cursor-pointer group"
                    onClick={() => {
                      if (!user) {
                        const viewedCalls = JSON.parse(sessionStorage.getItem('viewedCalls') || '[]');
                        if (viewedCalls.length >= 5 && !viewedCalls.includes(relatedCall.call_id)) {
                          navigate('/auth');
                          return;
                        }
                      }
                      navigate(`/chamada/${relatedCall.slug || relatedCall.call_id}`);
                    }}
                  >
                    {/* Badges */}
                    <div className="flex flex-wrap gap-2 mb-2">
                      <Badge className={`${getQualisClass(relatedCall.qualis_classification)} text-xs`}>
                        {relatedCall.qualis_classification}
                      </Badge>
                      {relatedCall.continuous_flow ? (
                        <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                          Fluxo Contínuo
                        </Badge>
                      ) : daysLeft !== null && daysLeft > 0 && daysLeft <= 30 && (
                        <Badge variant="outline" className={`text-xs ${daysLeft <= 7 ? 'bg-red-50 text-red-700 border-red-200' : 'bg-yellow-50 text-yellow-700 border-yellow-200'}`}>
                          {daysLeft} dias
                        </Badge>
                      )}
                    </div>
                    
                    {/* Title */}
                    <h4 className="font-semibold text-sm line-clamp-2 group-hover:text-[#002FA7] transition-colors mb-1">
                      {relatedCall.title}
                    </h4>
                    
                    {/* Journal */}
                    <p className="text-xs text-[#4B4C52] line-clamp-1 mb-2">
                      {relatedCall.journal_name}
                    </p>
                    
                    {/* Stats */}
                    <div className="flex items-center gap-3 text-xs text-[#4B4C52]">
                      <span className="flex items-center gap-1">
                        <Eye className="h-3 w-3" />
                        {relatedCall.views || 0}
                      </span>
                      {relatedCall.knowledge_areas?.length > 0 && (
                        <span className="truncate">
                          {relatedCall.knowledge_areas[0]}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* CTA to see more */}
            {!user && (
              <div className="mt-6 text-center">
                <p className="text-sm text-[#4B4C52] mb-3">
                  Descubra mais chamadas filtradas por área e QUALIS
                </p>
                <Button
                  onClick={() => navigate('/auth')}
                  className="bg-[#002FA7] hover:bg-[#001f75]"
                >
                  Criar conta gratuita
                </Button>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default CallDetailsPage;
